import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const TIMEOUT_MS = 20000;

const DISTRICT_TYPES = ["district", "locality"];

function mapsKey() {
  if (typeof document === "undefined" || !document.documentElement) {
    return "";
  }

  const dataset = document.documentElement.dataset;
  return dataset && dataset.mapsKey ? dataset.mapsKey : "";
}

function str(value) {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }

  if (typeof value === "number" && isFinite(value)) {
    return String(value);
  }

  return null;
}

function validCoordinate(lat, lng) {
  return typeof lat === "number" && typeof lng === "number"
    && isFinite(lat) && isFinite(lng)
    && lat >= -90 && lat <= 90
    && lng >= -180 && lng <= 180;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
    this.requestId = 0;
  }

  cancelAutocomplete() {
    // Jede neue Anfrage und jeder Abbruch macht ältere Antworten ungültig.
    this.requestId++;

    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.zip && g.district) {
      s = g.zip + " " + g.district;
    } else if (g.district) {
      s = g.district;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    } else if (g.label && !g.street) {
      p.push(g.label);
    }

    if (g.state && !g.street && g.state !== g.label) {
      p.push(g.state);
    }

    if (g.country && !g.street && g.country !== g.label) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  _geoaddress(l) {
    if (!l || typeof l !== "object" || !l.geometry || !Array.isArray(l.geometry.coordinates)) {
      return null;
    }

    // Koordinaten kommen als [lng, lat].
    const lng = l.geometry.coordinates[0];
    const lat = l.geometry.coordinates[1];
    if (!validCoordinate(lat, lng)) {
      return null;
    }

    // Feature nicht verändern, nur lesen.
    const props = l.properties && typeof l.properties === "object" ? l.properties : {};
    const type = str(props.type);
    const name = str(props.name);
    const osmKey = str(props.osm_key);
    const osmValue = str(props.osm_value);

    let street = str(props.street);
    const number = str(props.housenumber);
    const zip = str(props.postcode);
    let city = str(props.city);
    let district = str(props.district);

    if (type) {
      if (type === "street" && !street) {
        street = name;
      } else if (type === "city" && !city) {
        city = name;
      } else if (DISTRICT_TYPES.indexOf(type) !== -1 && !district && name !== city) {
        district = name;
      }
    } else {
      // Legacy-Fallback für Antworten ohne properties.type.
      if (osmKey === "highway" && !street) {
        street = name;
      } else if (name && city && name !== city && !district
        && (osmKey === "boundary" || (osmKey === "place" && osmValue === "suburb"))) {
        district = name;
      } else if (!city && osmKey === "place"
        && ["city", "town", "village", "hamlet"].indexOf(osmValue) !== -1) {
        city = name;
      }
    }

    if (district && district === city) {
      district = null;
    }

    const geoaddress = {};

    if (street) {
      geoaddress["street"] = street;
      if (number) {
        geoaddress["number"] = number;
      }
    }

    if (zip) {
      geoaddress["zip"] = zip;
    }

    if (city) {
      geoaddress["city"] = city;
    }

    if (district) {
      geoaddress["district"] = district;
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = str(props.state);
    geoaddress["country"] = str(props.country);

    // Name nur als Beschriftung, nicht als Stadt übernehmen.
    const label = name && name !== street && name !== city && name !== district ? name : null;

    geoaddress["text"] = this._geoaddressText({
      street: geoaddress.street,
      number: geoaddress.number,
      zip: geoaddress.zip,
      city: geoaddress.city,
      district: geoaddress.district,
      state: geoaddress.state,
      country: geoaddress.country,
      label
    });

    if (!geoaddress["text"]) {
      return null;
    }

    return geoaddress;
  }

  geocode(params) {
    const key = mapsKey();
    if (!key) {
      return Promise.resolve([]);
    }

    params.set("key", key);

    const request = this.axiosRequest;
    const requestId = this.requestId;

    return new Promise((resolve) => {
      this.axios.get(SEARCH_URL, {
        cancelToken: request ? request.source.token : undefined,
        timeout: TIMEOUT_MS,
        params
      }).then(res => {
        if (requestId !== this.requestId) {
          resolve(null);
          return;
        }

        const results = [];
        const features = res && res.data && Array.isArray(res.data.features) ? res.data.features : [];

        features.forEach(l => {
          const geoaddress = this._geoaddress(l);
          if (geoaddress) {
            results.push(geoaddress);
          }
        });

        resolve(results);
      }).catch((thrown) => {
        if (!this.axios.isCancel(thrown) && requestId === this.requestId) {
          console.warn("Geocoding fehlgeschlagen", thrown && thrown.message);
        }

        // Abbruch, Timeout und Fehler beenden die Anfrage ohne Treffer.
        resolve(requestId === this.requestId ? [] : null);
      }).finally(() => {
        if (this.axiosRequest === request) {
          this.axiosRequest = null;
        }
      });
    });
  }

  autocomplete(q) {
    this.cancelAutocomplete();

    const query = typeof q === "string" ? q.trim() : "";
    if (!query) {
      return Promise.resolve([]);
    }

    const CancelToken = this.axios.CancelToken;
    this.axiosRequest = {
      source: CancelToken.source()
    };

    const params = new URLSearchParams();
    params.append("q", query);
    params.append("lang", "de");
    params.append("limit", 10);

    return this.geocode(params);
  }

  reverse(lat, lng) {
    lat = Number(lat);
    lng = Number(lng);

    const key = mapsKey();
    if (!key || !validCoordinate(lat, lng)) {
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      this.axios.get(REVERSE_URL, {
        timeout: TIMEOUT_MS,
        params: {
          lat: lat,
          lon: lng,
          lang: "de",
          key: key
        }
      }).then(res => {
        const features = res && res.data && Array.isArray(res.data.features) ? res.data.features : [];
        resolve(features.length > 0 ? this._geoaddress(features[0]) : null);
      }).catch(err => {
        console.warn("Reverse-Geocoding fehlgeschlagen", err && err.message);
        resolve(null);
      });
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
